import { useState } from "react";
import { HandCoins, Users as UsersIcon } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, KpiCard, Loader, EmptyState, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { DebtorDialog } from "../components/DebtorDialog";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { money, dateTimeFmt } from "../lib/api";

export default function Debtors() {
  const { depotParam, scopeLabel } = useDepot();
  const [showSettled, setShowSettled] = useState(false);
  const [active, setActive] = useState(null);
  const { data, reload } = useApi("/debtors", { depot_id: depotParam, include_settled: showSettled });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Customer Credit" subtitle="Customers who bought on credit and what they still owe. Record payments as they come in."
        actions={
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Switch checked={showSettled} onCheckedChange={setShowSettled} data-testid="debtors-show-settled-switch" /> Show settled
          </label>
        } />
      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            <KpiCard label="Total owed to you" value={money(data.outstanding)} icon={HandCoins} tone="amber" testid="debtors-outstanding-total" />
            <KpiCard label="Customers owing" value={data.count} icon={UsersIcon} tone="slate" testid="debtors-count" delay={60} />
          </div>
          <SectionCard title={`${data.rows.length} customers`}>
            {!data.rows.length ? <EmptyState text="No unpaid credit. Credit sales appear here automatically." testid="debtors-empty" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="debtors-table">
                  <thead className={TABLE_HEAD}>
                    <tr>
                      <th className="px-4 py-2.5 text-left">Customer</th><th className="px-4 py-2.5 text-left">Depot</th>
                      <th className="px-4 py-2.5 text-right">On credit</th><th className="px-4 py-2.5 text-right">Paid</th>
                      <th className="px-4 py-2.5 text-right">Balance</th><th className="px-4 py-2.5 text-left">Last sale</th><th className="px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={`${r.customer_key}-${r.depot_id}`} className={TABLE_ROW} data-testid={`debtor-row-${i}`}>
                        <td className="px-4 py-2.5"><div className="font-medium">{r.customer}</div><div className="text-xs text-slate-500">{r.sales_count} credit sales</div></td>
                        <td className="px-4 py-2.5 text-slate-300">{r.depot_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{money(r.total_credit)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-400">{money(r.total_paid)}</td>
                        <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.balance > 0 ? "text-amber-400" : "text-slate-500"}`} data-testid={`debtor-balance-${i}`}>{money(r.balance)}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(r.last_sale)}</td>
                        <td className="px-2 py-2.5 text-right">
                          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setActive(r)} data-testid={`debtor-open-button-${i}`}>
                            {r.balance > 0 ? "Record payment" : "View"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
      <DebtorDialog row={active} onOpenChange={(o) => { if (!o) setActive(null); }} onChanged={reload} />
    </div>
  );
}
